import React, { useCallback, useEffect, useState } from "react";
import { Card, Col, Row, Select, Statistic, Table, Tooltip, Empty } from "antd";
import { useDispatch } from "react-redux";
import analyticsService from "../../services/analytics-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";

const RANGE_OPTIONS = [
  { value: 7, label: "7 วันล่าสุด" },
  { value: 30, label: "30 วันล่าสุด" },
  { value: 90, label: "90 วันล่าสุด" },
];

const numberFormat = (n) => (Number(n) || 0).toLocaleString("th-TH");

/**
 * First-party web analytics dashboard.
 * Data comes from nmu-management-service, which rolls up the cookieless
 * counters nmu-web writes to Redis. No third-party analytics is involved.
 */
const Analytics = () => {
  const dispatch = useDispatch();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);

  const load = useCallback(
    async (range) => {
      try {
        dispatch(appLoaderAction.enable());
        setSummary(await analyticsService.getSummary(range));
      } catch (err) {
        notifyUtitls.error(err);
      } finally {
        dispatch(appLoaderAction.disale());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    load(days);
  }, [days, load]);

  const history = (summary && summary.history) || [];
  const topPaths = (summary && summary.topPaths) || [];
  const maxViews = history.reduce((m, d) => Math.max(m, Number(d.pageViews) || 0), 0);

  const columns = [
    {
      title: "หน้า (Path)",
      dataIndex: "path",
      key: "path",
      /*
        NMU-582: a path is arbitrarily long and must not decide the table's width.

        These are real URLs and a Thai one arrives percent-encoded, so a single
        news article can be three hundred characters. With no constraint the cell
        grew to fit, the table grew with it, and the whole admin page picked up a
        horizontal scrollbar — the count column was pushed off the right edge of
        the card entirely.

        `ellipsis` also switches antd to table-layout: fixed, which is what makes
        the count column keep the 180px it asks for instead of being squeezed.
        showTitle is off because the browser's own title tooltip would sit under
        the antd one and show the same text twice.
      */
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip placement="topLeft" title={v}>
          <span id={`path-${v}`}>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: "จำนวนการเข้าชม",
      dataIndex: "views",
      key: "views",
      align: "right",
      width: 180,
      render: (v) => numberFormat(v),
    },
  ];

  return (
    <div id="analytics-page" className="p-3">
      <Row justify="space-between" align="middle" className="mb-3">
        <Col>
          <h4 className="mb-0">สถิติการเข้าชมเว็บไซต์</h4>
          <small className="text-muted">
            เก็บข้อมูลเองแบบไม่ใช้คุกกี้ และไม่ส่งข้อมูลผู้ใช้ออกภายนอก (PDPA-friendly)
          </small>
        </Col>
        <Col>
          <Select
            id="analytics-range"
            value={days}
            style={{ width: 180 }}
            onChange={(v) => setDays(v)}
          >
            {RANGE_OPTIONS.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="เข้าชมวันนี้"
              value={Number((summary && summary.pageViewsToday) || 0)}
              formatter={numberFormat}
            />
            <div id="stat-page-views-today" className="d-none">
              {(summary && summary.pageViewsToday) || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Tooltip title="ค่าประมาณด้วย HyperLogLog (คลาดเคลื่อน ~0.81%)">
              <Statistic
                title="ผู้เข้าชมไม่ซ้ำวันนี้"
                value={Number((summary && summary.uniqueVisitorsToday) || 0)}
                formatter={numberFormat}
              />
            </Tooltip>
            <div id="stat-unique-today" className="d-none">
              {(summary && summary.uniqueVisitorsToday) || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title={`รวมในช่วง ${days} วัน`}
              value={Number((summary && summary.pageViewsTotal) || 0)}
              formatter={numberFormat}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="เข้าชมสะสมทั้งหมด"
              value={Number((summary && summary.allTimePageViews) || 0)}
              formatter={numberFormat}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-3">
        <Col xs={24} lg={14}>
          <Card title="แนวโน้มรายวัน" id="analytics-trend">
            {history.length === 0 ? (
              <Empty description="ยังไม่มีข้อมูลย้อนหลัง" />
            ) : (
              <div className="d-flex align-items-end" style={{ height: 200, gap: 4 }}>
                {[...history]
                  .reverse()
                  .map((d) => {
                    const h = maxViews ? Math.max(4, ((Number(d.pageViews) || 0) / maxViews) * 180) : 4;
                    return (
                      <Tooltip
                        key={d.day}
                        title={`${d.day}: ${numberFormat(d.pageViews)} ครั้ง / ~${numberFormat(
                          d.uniqueVisitors
                        )} คน`}
                      >
                        <div
                          className="analytics-bar flex-fill"
                          style={{ height: h, background: "#1b341a", borderRadius: 3, minWidth: 6 }}
                        />
                      </Tooltip>
                    );
                  })}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="หน้าที่เข้าชมมากที่สุด" id="analytics-top-paths">
            <Table
              rowKey={(r) => r.path}
              columns={columns}
              dataSource={topPaths}
              pagination={false}
              size="small"
              locale={{ emptyText: "ยังไม่มีข้อมูล" }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;
